import React, { useState, useRef, useEffect, useMemo } from 'react';
import AppHeader from './AppHeader';
import BottomTabBar from './BottomTabBar';
import ChatMessage, { findRouteAsk, detectActions, hasCharterCard, isErrorMessage, extractCharterEditSeed, extractCharterTripType, extractCharterCardMeta } from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import RouteFormMulti, { buildMultiLegToken } from '../charter/RouteFormMulti';
import RouteFormRoundTrip from '../charter/RouteFormRoundTrip';
import { mapTokenLegs, mapRoundTripSeed } from '../charter/routeSeed';
import CharterQuoteForm from '../charter/CharterQuoteForm';
import { submitCharterForm } from '../charter/charterFormApi';
import ProfilePage from '../pages/ProfilePage';
import MyQuotesPage from '../pages/MyQuotesPage';
import MyRidesPage from '../pages/MyRidesPage';
import { sendMessage } from '../services/agentApi';
import { getCurrentSession } from '../services/authService';
import { ToastProvider } from '../ui/primitives';
import { cx } from '../ui/cx';
import { IconBot } from '../ui/icons';
import { Plus, ClipboardList, FileText, Car, User } from 'lucide-react';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

// R3 vs Peoria branding + the chat run target now come from the PATH (resolveProduct) as the `brand`
// and `runConfig` props, so one bundle serves both /r3chat and /peoriachat — no build-time env split.
//
// Charter RouteFormMulti onConfirm (see the mount below): it pushes a rider-facing itinerary sentence,
// then SENDS the sentence + a reverse-route token `[[ROUTE=<compact-json legs[]>]]` (built via
// buildMultiLegToken) as the LAST thing in the string — the token never appears in the bubble. The
// agents side routes the legs[] through merge_verified_itinerary (VERIFIED slots) and strips it.

const TABS = [
  { id: 'book', label: 'Book', icon: <Car size={20} /> },
  { id: 'quotes', label: 'My quotes', icon: <FileText size={20} /> },
  { id: 'rides', label: 'My rides', icon: <ClipboardList size={20} /> },
  { id: 'profile', label: 'Profile', icon: <User size={20} /> },
];

function getWelcomeMessage(name, isCharter) {
  const greeting = name ? `Hi ${name}` : 'Hi';
  // Charter leads with CONTACT (not the trip) so Peoria keeps a usable lead even if the customer leaves
  // mid-conversation. This must match the server opening (one_way_messages.MSG_OPENING) — the client
  // seeds it and the agent dedups against it.
  const intro = isCharter
    ? "Welcome to Peoria Charter! Before we get into your trip, who should Peoria Charter follow up with? Please share your name, phone number, and email — and the group or organization, if there is one."
    : `${greeting}, welcome to R3 Services! How can I assist you today?`;
  return { role: 'assistant', content: intro };
}

function CustomerApp({ authSession, setAuthSession, userName, onLogout, anonymous = false, brand, runConfig }) {
  // Product branding + run target are path-derived (resolveProduct) and passed in as props.
  const isCharter = !!(brand && brand.isCharter);
  const [activeTab, setActiveTab] = useState('book');
  const [messages, setMessages] = useState(() => {
    const name = userName || authSession?.user?.given_name || authSession?.user?.name || '';
    return [getWelcomeMessage(name, isCharter)];
  });
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  // Peoria charter fallback: when the chat can't understand something, the rider can toggle to a
  // deterministic static quote form (CharterQuoteForm). Charter-only; the chat stays the default view.
  const [formOpen, setFormOpen] = useState(false);
  // Which tool is currently running (drives the status chip), or null.
  const [inputPrefill, setInputPrefill] = useState('');
  const [routeOpen, setRouteOpen] = useState(false);
  // UNIFIED FORM: every trip type opens the ONE consolidated route form (RouteFormMulti), so popupLeg
  // is always 'multi' — kept as state (rather than removed) to gate the render + for future variants.
  const [popupLeg, setPopupLeg] = useState('multi');
  // Bumped on every confirm so the form remounts with fresh seeds next open (RouteFormMulti reads
  // `initialLegs` at mount only).
  const [formNonce, setFormNonce] = useState(0);
  // The rider's last CONFIRMED itinerary (the raw legs array), under the 'multi' key — kept so
  // re-opening the form pre-fills the legs (edit, not re-enter) and so we know a later confirm is an
  // UPDATE vs a first set.
  const [confirmedRoutes, setConfirmedRoutes] = useState({});
  // Bumped on "New chat" to force-remount the route form. Its legs live in its own useState (seeded
  // from `initialLegs` only at mount) and it stays mounted across opens, so clearing confirmedRoutes
  // alone wouldn't wipe the fields — the remount does.
  const [chatNonce, setChatNonce] = useState(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleNewChat = () => {
    const name = userName || authSession?.user?.given_name || authSession?.user?.name || '';
    setMessages([getWelcomeMessage(name, isCharter)]);
    setSessionId(null);
    setActiveTab('book');
    // New conversation → drop the confirmed route and remount the route popup so its
    // From/To/Stops fields don't carry over from the previous quote.
    setConfirmedRoutes({});
    setRouteOpen(false);
    setPopupLeg(1);
    setFormNonce(0);
    setChatNonce((n) => n + 1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // 'rides' and 'quotes' tabs render dedicated list pages that fetch their own data.
  };

  const handleBookQuote = (quoteNumber) => {
    setActiveTab('book');
    setInputPrefill(`Book quote ${quoteNumber}`);
  };

  // Returns the final assistant content string for this turn (or null on
  // session-expiry / error). The fire-and-forget ChatInput caller ignores the
  // return; the payment flow awaits it to read back the confirmed booking card.
  const handleSend = async (text, { hideUserMessage = false } = {}) => {
    let token = authSession?.idToken;
    // Anonymous webchat: no Cognito session — send with no token / no user_id. The agent
    // collects name+phone in-chat and the backend identifies the rider by those.
    if (!anonymous) {
      try {
        const freshSession = await getCurrentSession();
        token = freshSession.idToken;
        setAuthSession(freshSession);
      } catch (e) {
        setAuthSession(null);
        return null;
      }
    }

    setInputPrefill('');
    if (!hideUserMessage) {
      setMessages((prev) => [...prev, { role: 'user', content: text }]);
    }
    setLoading(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    let finalContent = null;
    await sendMessage(
      text, sessionId, authSession?.user?.sub || null, token,
      (content, sid) => {
        finalContent = content;
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content };
          return updated;
        });
        if (sid) setSessionId(sid);
      },
      (sid) => { if (sid) setSessionId(sid); setLoading(false); },
      (err) => {
        finalContent = null;
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: `Sorry, something went wrong: ${err}` };
          return updated;
        });
        setLoading(false);
      },
      // onStatus: still consumed by agentApi's signature, but we no longer render a per-tool
      // status chip — the thinking bubble shows animated dots until the text streams in.
      () => {},
      0,          // depth
      runConfig   // path-derived {teamId, agentId} — R3 team/route-once vs Peoria direct-agent
    );

    return finalContent;
  };

  const handleFeedback = async (msg, rating, feedbackText) => {
    // Find the user prompt that preceded this assistant message
    const msgIndex = messages.indexOf(msg);
    let prompt = '';
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') { prompt = messages[i].content; break; }
    }

    try {
      let token = authSession?.idToken;
      try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}

      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_BASE}/api/chat-feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userId: authSession?.user?.sub,
          sessionId,
          prompt,
          response: msg.content,
          rating,
          feedbackText: feedbackText || null,
          pageContext: activeTab,
        }),
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  // Peoria charter: when an assistant message carries [[ROUTE_FORM]] (or a leg-tagged variant), the
  // agent is asking for that trip's exact route — the "Set your route on a map" popup is offered
  // INSIDE that message bubble as a chip (ChatMessage renders it under the question it belongs to).
  // Deliberately a PROMPT, not a modal takeover: the popup never opens itself. findRouteAsk scans
  // BACKWARDS for the most recent un-consumed ask (stopping at a form-confirm receipt or a quote
  // card), so a rider who types while the form is pending keeps a tappable chip on the ask bubble —
  // checking only the LAST message stranded them (the "boi" bug: the steer said "open the form" but
  // the chip was gone). A :multi ask can carry a PRE-SEED (legs[]) so the form opens pre-filled; a
  // :round_trip ask carries an {outbound,return,…} seed for the dedicated two-leg form.
  const routeAsk = useMemo(() => {
    const none = { index: -1, leg: 1, legs: null, roundTrip: null };
    if (!isCharter || messages.length === 0) return none;   // Peoria charter only
    return findRouteAsk(messages);
  }, [messages, isCharter]);
  const routeAskIndex = routeAsk.index;

  // Peoria charter: the latest assistant turn may carry [[ACTIONS=…]] — the contextual chips for this
  // turn. Only that message gets `onAction` so chips never linger on older bubbles.
  const actionsAskIndex = useMemo(() => {
    if (!isCharter || messages.length === 0) return -1;   // Peoria charter only
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant' || !last.content) return -1;
    return detectActions(last.content) ? messages.length - 1 : -1;
  }, [messages, isCharter]);

  // The LATEST assistant message that renders a charter quote card — the only card that gets the
  // "Edit route on map" affordance (editing always edits the CURRENT route; stale cards stay static).
  const lastQuoteIndex = useMemo(() => {
    if (!isCharter) return -1;   // Peoria charter only
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant' && hasCharterCard(messages[i].content)) return i;
    }
    return -1;
  }, [messages, isCharter]);

  // The itinerary SEED embedded in the latest quote card, so "Edit itinerary" reopens the ONE route
  // form PRE-FILLED for EVERY trip type (the route-ask seed only rides on map-turn messages; a card is
  // where an edit-after-quote starts). Falls back to null when no card has an editSeed.
  const latestCardSeed = useMemo(
    () => (lastQuoteIndex >= 0 ? extractCharterEditSeed(messages[lastQuoteIndex].content) : null),
    [messages, lastQuoteIndex]);

  // The latest quote card's trip type — so "Edit itinerary" on a ONE-WAY card reopens the one-way form
  // (same form the rider edited in-flow), not the multi-leg one with a round-trip toggle / add-leg.
  const latestCardTripType = useMemo(
    () => (lastQuoteIndex >= 0 ? extractCharterTripType(messages[lastQuoteIndex].content) : null),
    [messages, lastQuoteIndex]);

  // The resolved multi-leg route-form seed. Prefer the agent's FRESH map-turn seed (routeAsk.legs): it
  // reflects the LATEST itinerary — a chat correction that REPLACED a leg, or the VERIFIED addresses after
  // a prior confirm — and only rides on a NEW assistant message that post-dates any confirm. This is what
  // makes a route CHANGE UPDATE the one form (RouteFormMulti) instead of leaving a stale confirmed route
  // in place / reading as "another form". Fall back to the last confirm this session (the bridge while the
  // confirm's response is still loading and routeAsk.legs is null), then the latest quote card's editSeed.
  const multiSeed = useMemo(
    () => mapTokenLegs(routeAsk.legs) || confirmedRoutes.multi || mapTokenLegs(latestCardSeed),
    [confirmedRoutes.multi, routeAsk.legs, latestCardSeed]);
  // A signature of that seed, mixed into the form's `key`. RouteFormMulti reads `initialLegs` only at
  // mount and stays mounted across opens, so a FRESHER agent seed (e.g. a destination captured a turn
  // late) would otherwise never reflow in. A changed signature remounts the form with the new seed. This
  // never wipes in-progress edits: a fresher `routeAsk` only appears with a NEW assistant message, which
  // only lands while the modal is CLOSED (sending requires it closed); while open the seed is stable.
  const multiSeedSig = useMemo(() => {
    try { return JSON.stringify(multiSeed || null); } catch { return '0'; }
  }, [multiSeed]);

  // The dedicated ROUND-TRIP form seed. Prefer the agent's fresh [[ROUTE_FORM:round_trip={…}]] map-turn
  // seed; fall back to the latest quote card's editSeed (for edit-after-quote), reshaped to two legs.
  // The round-trip card's shared meta (passenger count + coach note) — editSeed carries only the legs.
  const latestCardMeta = useMemo(
    () => (lastQuoteIndex >= 0 ? extractCharterCardMeta(messages[lastQuoteIndex].content) : null),
    [messages, lastQuoteIndex]);
  const roundTripSeed = useMemo(() => {
    if (routeAsk.roundTrip) return mapRoundTripSeed(routeAsk.roundTrip);
    // A round-trip quote card's editSeed is a two-item legs[] array [outbound, return]; reshape it,
    // folding in the passenger count + coach note (which live on the card, not in editSeed) so the
    // reopened form is fully seeded — otherwise the empty passenger count would disable "Confirm".
    if (latestCardTripType === 'round_trip' && Array.isArray(latestCardSeed) && latestCardSeed.length >= 2) {
      return mapRoundTripSeed({
        outbound: latestCardSeed[0], return: latestCardSeed[1],
        passengerCount: latestCardMeta ? latestCardMeta.passengerCount : '',
        coachDisposition: latestCardMeta ? latestCardMeta.coachDisposition : '',
      });
    }
    return null;
  }, [routeAsk.roundTrip, latestCardTripType, latestCardSeed, latestCardMeta]);
  const roundTripSeedSig = useMemo(() => {
    try { return JSON.stringify(roundTripSeed || null); } catch { return '0'; }
  }, [roundTripSeed]);

  // The SHUTTLE form seed (RouteFormMulti shuttle variant): the agent's [[ROUTE_FORM:shuttle={…}]]
  // map-turn seed, else the last confirm this session (the bridge while the confirm's response is
  // still loading), else the latest shuttle card's editSeed — token legs[] inverted via the SAME
  // mapTokenLegs every other form seeds through, so the shuttle endpoints get the identical
  // name/city/state/candidates prefill shape (and PlaceField's chat-named-place resolve) as one-way.
  const shuttleSeed = useMemo(() => {
    if (routeAsk.leg === 'shuttle' && routeAsk.legs) return mapTokenLegs(routeAsk.legs);
    if (confirmedRoutes.shuttle) return confirmedRoutes.shuttle;
    if (latestCardTripType === 'shuttle' && Array.isArray(latestCardSeed) && latestCardSeed.length) {
      return mapTokenLegs(latestCardSeed);
    }
    return null;
  }, [routeAsk.leg, routeAsk.legs, confirmedRoutes.shuttle, latestCardTripType, latestCardSeed]);
  const shuttleSeedSig = useMemo(() => {
    try { return JSON.stringify(shuttleSeed || null); } catch { return '0'; }
  }, [shuttleSeed]);

  // Peoria charter: true when the rider is stuck on an error/failure turn — surfaces the inline
  // "switch to the quote form" prompt (the chat proactively pointing at the toggle). Latest assistant
  // turn only, and not while a reply is still streaming.
  const showFormPrompt = useMemo(() => {
    if (!isCharter || formOpen || loading || messages.length === 0) return false;
    const last = messages[messages.length - 1];
    return last.role === 'assistant' && isErrorMessage(last.content);
  }, [isCharter, formOpen, loading, messages]);

  // Submit the static quote form. Owns the session id (mints one if the rider opened the form before
  // chatting) and the transcript so the captured fields + the conversation so far land on the SAME
  // charter_lead the dispatcher reads. Returns { ok, error } so the form can show a failure inline.
  const handleFormSubmit = async (values) => {
    let sid = sessionId;
    if (!sid) {
      sid = `charter-form-${(window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID() : `${Date.now()}-${Math.round(Math.random() * 1e9)}`}`;
      setSessionId(sid);
    }
    // The persisted transcript is the source of truth; this client copy only seeds a lead that has
    // no turns yet (form opened before any chat turn landed). Drop the empty typing placeholder.
    const transcript = messages
      .filter((m) => m.content && String(m.content).trim())
      .map((m) => ({ role: m.role, text: m.content }));

    const res = await submitCharterForm({ sessionId: sid, values, transcript });
    if (res && res.ok) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "Thanks — your request is with our Peoria Charter team, and a dispatcher will follow "
          + "up with your quote shortly. Anything else I can help with in the meantime?",
      }]);
      setFormOpen(false);
    } else {
      // Keep the friendly inline message in the form, but log the specific reason for diagnosis
      // (e.g. a 404 when the agents endpoint isn't deployed, or ALREADY_BOOKED / SAVE_FAILED).
      console.warn('[charter] quote form submit failed:', res && res.error);
    }
    return res || { ok: false, error: 'NETWORK' };
  };

  // Open the ONE consolidated route form for an edit — same behavior as the quote card's "Edit itinerary"
  // link, so the chip and the card link are interchangeable. Trip type picks the round-trip vs multi form.
  const openCharterEditForm = () => {
    setPopupLeg(latestCardTripType === 'round_trip' ? 'round_trip'
      : latestCardTripType === 'shuttle' ? 'shuttle' : 'multi');
    setRouteOpen(true);
  };

  // Contextual action-chip dispatcher (see ChatMessage [[ACTIONS=…]] chips). Edit/confirm open the route
  // form; the rest send a canned phrase the deterministic charter planner already recognizes — so a click
  // is a reliable stand-in for typing, not another intent the classifier has to get right.
  const handleCharterAction = (id) => {
    if (id === 'edit_itinerary' || id === 'edit_route') { openCharterEditForm(); return; }
    if (id === 'add_stop') { handleSend('add a stop'); return; }
    // Shuttle vocabulary: same add-stop path agent-side, but the phrase matches the shuttle flow's
    // own words (extra pickup spots on the loop).
    if (id === 'add_pickup') { handleSend('add a pickup spot'); return; }
    if (id === 'send_request') { handleSend('send request'); return; }
    if (id === 'continue') { handleSend('continue'); return; }
  };

  const renderContent = () => {
    if (activeTab === 'profile') {
      return <ProfilePage authSession={authSession} onLogout={onLogout} />;
    }
    if (activeTab === 'quotes') {
      return <MyQuotesPage authSession={authSession} onBookQuote={handleBookQuote} />;
    }
    if (activeTab === 'rides') {
      return <MyRidesPage authSession={authSession} />;
    }

    // Charter fallback: the quote form takes over the book tab while the toggle is on "Quote form".
    if (isCharter && formOpen) {
      return (
        <CharterQuoteForm
          onSubmit={handleFormSubmit}
          onCancel={() => setFormOpen(false)}
        />
      );
    }

    // 'book' tab shows the chat window
    return (
      <>
        <div className="chat-area">
          {messages.length > 1 && (
            <div className="chat-clear-bar">
              <button className="chat-clear-btn" onClick={handleNewChat}>
                <Plus size={16} strokeWidth={2.5} /> New chat
              </button>
            </div>
          )}
          {messages.map((msg, i) => {
            // While loading, the trailing assistant message is an empty
            // placeholder — skip it so we show the typing bubble below instead
            // of an empty bubble + detached dots.
            const isEmptyPlaceholder =
              msg.role === 'assistant' && !msg.content && i === messages.length - 1;
            if (isEmptyPlaceholder) return null;
            return (
              <ChatMessage
                key={i}
                message={msg}
                isCharter={isCharter}
                // Peoria only: the last assistant bubble types out its text (client-side typewriter).
                // Older messages + R3 render instantly. `loading` while it streams in.
                streamIn={isCharter && msg.role === 'assistant' && i === messages.length - 1}
                onFeedback={handleFeedback}
                // UNIFIED FORM: every trip type — a map-turn ask OR an edit-after-quote — opens the ONE
                // consolidated route form (popupLeg 'multi'); it prefills from the itinerary seed.
                onRouteRequest={i === routeAskIndex && !loading
                  ? () => {
                      setPopupLeg(routeAsk.leg === 'round_trip' ? 'round_trip'
                        : routeAsk.leg === 'shuttle' ? 'shuttle' : 'multi');
                      setRouteOpen(true);
                    }
                  : undefined}
                // "Edit itinerary" stays on the LATEST quote card at all times (even while a reply streams
                // in) so the rider can always jump back to the form — the post-quote flow tells them to
                // scroll up to it. (onRouteRequest keeps its !loading guard; that's a mid-turn map ask.)
                onEditRoute={i === lastQuoteIndex ? openCharterEditForm : undefined}
                // Contextual action chips ([[ACTIONS=…]]) — only the latest assistant turn gets the handler,
                // so chips never linger on older bubbles. Suppressed while a reply streams in.
                onAction={i === actionsAskIndex && !loading ? handleCharterAction : undefined}
                // ONE canonical quote: an earlier charter estimate card is superseded once a newer one
                // exists below (only the latest renders in full) — the rider sees a single updating quote.
                superseded={isCharter && msg.role === 'assistant'
                  && hasCharterCard(msg.content) && i < lastQuoteIndex}
              />
            );
          })}
          {loading && messages[messages.length - 1]?.content === '' && (
            <div className="flex flex-col mb-4 px-4 items-start">
              <div className="flex items-center gap-1.5 mb-1">
                <div className={cx('w-8 h-8 rounded-full text-white flex items-center justify-center shadow-card',
                  isCharter ? 'bg-peoria-red' : 'bg-primary')}>
                  <IconBot size={16} />
                </div>
                <span className={cx('text-sm font-semibold', isCharter ? 'text-peoria-text' : 'text-primary')}>Assistant</span>
              </div>
              <div
                className={cx('max-w-[80%] px-4 py-3 rounded-card rounded-bl-md shadow-card',
                  isCharter ? 'bg-peoria-surface border border-peoria-border' : 'bg-white border border-border')}
                role="status"
                aria-live="polite"
              >
                {/* Always show the animated dots while thinking, then hand straight off to the
                    streaming assistant text — we never surface a per-tool "Working…" status chip. */}
                <div className="flex items-center gap-1">
                  <div className="dot" /><div className="dot" /><div className="dot" />
                </div>
              </div>
            </div>
          )}
          {showFormPrompt && (
            <div className={cx('mx-4 mb-4 flex items-start gap-3 rounded-card border px-4 py-3',
              isCharter ? 'border-peoria-red/40 bg-peoria-red/10' : 'border-accent/40 bg-accent/10')}>
              <div className="flex-1">
                <p className={cx('text-13 font-semibold', isCharter ? 'text-peoria-text' : 'text-primary')}>Having trouble?</p>
                <p className={cx('text-13 mt-0.5', isCharter ? 'text-peoria-muted' : 'text-body/70')}>
                  You can fill out a quick quote form instead — everything you've told me so far comes
                  along with it.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className={cx('shrink-0 self-center rounded-btn border px-3 py-1.5 text-13 font-medium transition-colors',
                  isCharter
                    ? 'border-peoria-border bg-peoria-surface2 text-peoria-text hover:border-peoria-red/60 hover:bg-peoria-red/10'
                    : 'border-primary/40 bg-white text-primary hover:bg-primary/5 hover:border-primary/60')}
              >
                Switch to the quote form →
              </button>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <ChatInput onSend={handleSend} disabled={loading} prefill={inputPrefill} isCharter={isCharter} />
        {/* MULTI-LEG form (the whole itinerary at once: add/remove/reorder legs + per-leg date/time).
            Opened when the agent asks with [[ROUTE_FORM:multi]] (detectRouteRequest -> leg:'multi').
            On confirm it sends a legs[] token routed through merge_verified_itinerary (agents side). */}
        {isCharter && popupLeg === 'multi' && (
          <RouteFormMulti
            key={`${chatNonce}:multi:${formNonce}:${multiSeedSig}`}
            isOpen={routeOpen}
            onClose={() => setRouteOpen(false)}
            // Seed order: a prior confirm in this session, else the agent's [[ROUTE_FORM:multi={…}]]
            // map-turn seed, else the latest quote card's editSeed (so edit-after-quote is prefilled too).
            // The seed signature in `key` re-mounts the (closed) form when a fresher seed arrives.
            initialLegs={multiSeed}
            // ONE-WAY mode: a single leg by definition — hide the round-trip toggle and "Add another leg".
            // True on a one-way map-turn ask OR when editing a one-way quote card (same form throughout).
            oneWay={routeAsk.leg === 'one_way' || latestCardTripType === 'one_way'}
            onConfirm={(routeText, legs) => {
              setRouteOpen(false);
              const wasUpdate = !!confirmedRoutes.multi;
              setConfirmedRoutes((m) => ({ ...m, multi: legs }));   // keep the raw legs to re-seed an edit
              setFormNonce((n) => n + 1);
              // routeReceipt marks this turn as a form-confirm receipt — findRouteAsk stops its
              // backward scan here so an ask that PRE-DATES the confirm can't resurrect its stale seed.
              setMessages((prev) => [...prev, { role: 'user', content: routeText, routeUpdated: wasUpdate, routeReceipt: true }]);
              // The legs[] array IS the authoritative itinerary — no `leg:` tag (the agents side detects
              // the array and routes it through merge_verified_itinerary: add/remove/reorder replace).
              const token = `[[ROUTE=${JSON.stringify(buildMultiLegToken(legs))}]]`;
              handleSend(`${routeText} ${token}`, { hideUserMessage: true });
            }}
          />
        )}
        {/* ROUND-TRIP form (fixed two legs — Outbound + Return; the return ends at the original
            departure). Opened for [[ROUTE_FORM:round_trip]] (detectRouteRequest -> leg:'round_trip')
            or editing a round-trip quote card. Confirm sends an {outbound,return,…} token routed
            through _route_verified_slots' outbound/return branch on the agents side. */}
        {isCharter && popupLeg === 'round_trip' && (
          <RouteFormRoundTrip
            key={`${chatNonce}:round_trip:${formNonce}:${roundTripSeedSig}`}
            isOpen={routeOpen}
            onClose={() => setRouteOpen(false)}
            initial={roundTripSeed}
            onConfirm={(routeText, token) => {
              setRouteOpen(false);
              setFormNonce((n) => n + 1);
              // routeReceipt: a form-confirm receipt — stops findRouteAsk's backward scan (see above).
              setMessages((prev) => [...prev, { role: 'user', content: routeText, routeReceipt: true }]);
              const wire = `[[ROUTE=${JSON.stringify(token)}]]`;
              handleSend(`${routeText} ${wire}`, { hideUserMessage: true });
            }}
          />
        )}
        {/* SHUTTLE form (single pickup ↔ drop-off + first service day & first pickup time) — the SAME
            modern surface as one-way (RouteFormMulti, shuttle variant: relabelled single leg, stops
            editor hidden, Places prefill off the seed). Opened for [[ROUTE_FORM:shuttle]]
            (detectRouteRequest -> leg:'shuttle') or editing a shuttle quote card. Confirm sends the
            SAME single-leg legs[] token as the one-way flow (buildMultiLegToken with one leg); the
            agents map the leg's departDateTime into shuttle_start_date / shuttle_out_time. */}
        {isCharter && popupLeg === 'shuttle' && (
          <RouteFormMulti
            shuttle
            key={`${chatNonce}:shuttle:${formNonce}:${shuttleSeedSig}`}
            isOpen={routeOpen}
            onClose={() => setRouteOpen(false)}
            initialLegs={shuttleSeed}
            onConfirm={(routeText, legs) => {
              setRouteOpen(false);
              setConfirmedRoutes((m) => ({ ...m, shuttle: legs }));  // re-seed a later edit
              setFormNonce((n) => n + 1);
              // routeReceipt: a form-confirm receipt — stops findRouteAsk's backward scan (see above).
              setMessages((prev) => [...prev, { role: 'user', content: routeText, routeReceipt: true }]);
              const wire = `[[ROUTE=${JSON.stringify(buildMultiLegToken(legs))}]]`;
              handleSend(`${routeText} ${wire}`, { hideUserMessage: true });
            }}
          />
        )}
      </>
    );
  };

  return (
    <ToastProvider>
      {/* Peoria opts into the dark theme via the `app--peoria` modifier (scoped CSS in App.css)
          and `isCharter`-gated Tailwind classes below. R3 keeps the plain `.app` / navy classes. */}
      <div className={cx('app', isCharter && 'app--peoria')} data-brand={isCharter ? 'peoria' : undefined}>
        <AppHeader
          userName={userName}
          onLogout={onLogout}
          subtitle={isCharter ? 'CHARTER QUOTES' : 'BOOK A RIDE'}
          logoSrc={isCharter ? '/peoria-charter-logo.svg' : undefined}
          brandName={isCharter ? 'Peoria Charter' : undefined}
          variant={isCharter ? 'peoria' : 'customer'}
        />
        {isCharter && activeTab === 'book' && (
          <div className="shrink-0 border-b border-peoria-border bg-peoria-bg px-4 py-2">
            <div className="mx-auto flex max-w-2xl gap-1 rounded-btn bg-peoria-surface2 p-1">
              {[
                { key: 'chat', label: 'Chat', active: !formOpen },
                { key: 'form', label: 'Quote form', active: formOpen },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setFormOpen(t.key === 'form')}
                  className={cx(
                    'flex-1 rounded-md px-3 py-1.5 text-13 font-medium transition-colors',
                    t.active
                      ? 'bg-peoria-red text-white shadow-card'
                      : 'text-peoria-muted hover:text-peoria-text'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {renderContent()}
        {/* Anonymous webchat is chat-only: My quotes / My rides / Profile need a userId,
            which a no-login rider doesn't have, so hide the tab bar entirely. */}
        {!anonymous && (
          <BottomTabBar tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />
        )}
      </div>
    </ToastProvider>
  );
}

export default CustomerApp;
